import {
  Button,
  Checkbox,
  createStyles,
  FormControlLabel,
  Grid,
  Paper,
  Theme,
  Typography,
  withStyles
} from "@material-ui/core";
import * as React from "react";
import * as queryString from 'query-string';
import OrganizationCommunity from "../../organization/models/OrganizationCommunity";
import { identity, isAdmin } from "../../core/auth/identityAuth";
import Loading from "../../shared/components/loading/Loading";
import { getCommunityConfig, getMyMemberships, requestCommunityMembership } from "../api/communitiesApi";
import ICommunityConfig from "../types/ICommunityConfig";
import RequestMembershipConfirmation from "./RequestMembershipConfirmation";
import { OrganizationCommunityMembershipStatus } from "../../organization/models/OrganizationCommunityMembershipStatus";
import Alert from "../../shared/components/Alert";

const SupportUrl = process.env.REACT_APP_NXTPORT_COMMUNITIES_SUPPORT_URL || '';

const styles = (theme: Theme) => createStyles({
  link: {
    color: theme.palette.secondary.dark,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  logo: {
    height: 150,
    marginBottom: theme.spacing.unit * 3
  },
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  paper: {
    width: '50%',
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit * 3
  },
  button: {
    marginRight: theme.spacing.unit * 3
  }
});

interface IState {
  communityCode: string;
  isLoading: boolean;
  existingMembership: OrganizationCommunity | undefined,
  communityConfig: ICommunityConfig | undefined,
  termsAndConditionsChecked: boolean,
  requestMembershipSuccess: boolean,
  loadDataError: boolean,
  requestMembershipError: boolean
}

class RequestCommunityMembership extends React.Component<any, IState> {
  constructor(props: any) {
    super(props);
    const queryObject = queryString.parse(window.location.search);
    this.state = {
      isLoading: false,
      communityCode: queryObject.nxtCommunity,
      existingMembership: undefined,
      communityConfig: undefined,
      termsAndConditionsChecked: false,
      requestMembershipSuccess: false,
      loadDataError: false,
      requestMembershipError: false
    }

    this.onRequestMembership = this.onRequestMembership.bind(this);
    this.onChangeTermsAndConditionsChecked = this.onChangeTermsAndConditionsChecked.bind(this);
  }

  public componentDidMount() {
    this.loadData();
  }

  public communityConfigIsIncomplete(communityConfig: ICommunityConfig): boolean {
    return !communityConfig ||
      (!communityConfig.tcUrl
        || !communityConfig.name
        || !communityConfig.code
        || !communityConfig.description
        || !communityConfig.logoUrl
        || !communityConfig.returnUrl);
  }

  public async loadData() {
    this.setState({ isLoading: true })
    
    try {
      const [existingMemberships, communityConfig] = await Promise.all([
        getMyMemberships(),
        getCommunityConfig(this.state.communityCode),
      ]);
      
      if (this.communityConfigIsIncomplete(communityConfig)){
        throw new Error('Community config from sitecore is incomplete.')
      }
      
      this.setState({
        isLoading: false,
        existingMembership: existingMemberships.find((community: OrganizationCommunity) => community.communityCode === this.state.communityCode),
        communityConfig
      })
    } catch (err) {
      this.setState({
        isLoading: false,
        loadDataError: true
      })
    }
  }

  public async onRequestMembership() {
    this.setState({ isLoading: true })

    try {
      await requestCommunityMembership(this.state.communityCode);
      this.setState({
        isLoading: false,
        requestMembershipSuccess: true
      });
    } catch (err) {
      this.setState({
        isLoading: false,
        requestMembershipError: true
      })
    }

  }

  public onChangeTermsAndConditionsChecked(event: any) {
    this.setState({
      termsAndConditionsChecked: event.target.checked
    });
  }

  public renderContent() {
    const { classes } = this.props;
    const { communityConfig, communityCode, loadDataError, requestMembershipError, termsAndConditionsChecked, existingMembership } = this.state;
    
    if (loadDataError) {
      return <div style={{ marginTop: '24px', marginBottom: '24px' }}>
        <Alert
          severity='error'
          title={`Something went wrong`}
        >
          <Typography color="inherit">
            Something went wrong while loading the community. Please try
            again later. If the problem persists, please <a href={SupportUrl}>contact NxtPort support.</a>
          </Typography>
        </Alert>
      </div>
    }
    
    if (!communityCode || !communityConfig) {
      return <Typography>This community is not known.</Typography>
    }

    if (!isAdmin) {
      return (
        <>
          <Typography>You do not have access to this page because you are not an admin of
            the {identity["nxt:entity:name"]} organization. Please contact the administrator of your
            organization.</Typography>
          <Typography>If you do not know the administrator of your organisation, please
            <a href={SupportUrl} className={classes.link}> create a support ticket.</a>
          </Typography>
        </>
      )
    }

    if (existingMembership && existingMembership.status === OrganizationCommunityMembershipStatus.Active) {
      return <Typography>You already are a member of the {communityConfig.name} community!</Typography>
    } else if (existingMembership) {
      return (
        <>
          <Typography>There is already a membership for your entity for
            the {communityConfig.name} community.</Typography>
          <Typography>The membership status is currently {existingMembership.status}.</Typography>
        </>)
    }
    
    // show actual request membership screen
    return (
      <>
        <Grid item>
          <Typography>{communityConfig.description}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                name='termsAndConditionsCheckbox'
                checked={this.state.termsAndConditionsChecked}
                onChange={this.onChangeTermsAndConditionsChecked}
                color="primary"
              />
            }

            label={
              <span>I accept the <a href={communityConfig.tcUrl} target="_blank">community's terms &amp; conditions</a>
                .</span>
            }
          />

          {requestMembershipError &&
            <div style={{ marginTop: '24px', marginBottom: '24px' }}>
              <Alert
                severity='error'
                title={`Something went wrong.`}
              >
                <Typography color="inherit">
                  Something went wrong while requesting the membership. Please try
                  again later. If the problem persists, please <a href={SupportUrl}>contact NxtPort support.</a>
                </Typography>
              </Alert>
            </div>
          }
        </Grid>

        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={this.onRequestMembership} 
            disabled={this.state.loadDataError || !termsAndConditionsChecked}
          >
            Request membership
          </Button>
          <Button
            className={classes.button}
            onClick={() => window.location.assign("/")}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
        </Grid>
      </>
    )
  }

  public render() {
    const { classes } = this.props;
    const { communityConfig, isLoading } = this.state;
    
    if (isLoading) {
      return <Grid direction='column' container className={classes.container}>
        <Loading/>
      </Grid>
    }

    if (this.state.requestMembershipSuccess) {
      return <RequestMembershipConfirmation communityConfig={communityConfig} classes={classes}/>
    }

    return <Grid direction='column' container className={classes.container}>
      {communityConfig && communityConfig.logoUrl &&
        <Grid item>
          <img
            src={communityConfig.logoUrl}
            className={classes.logo}
          />
        </Grid>}

      <Grid item>
        <Typography variant="title">
          Membership request
        </Typography>
        <Typography color="textSecondary" gutterBottom={true}>
          {communityConfig ? communityConfig.name : null}
        </Typography>
      </Grid>

      <Grid item className={classes.paper}>
        <Paper className="padding-24">

          {this.renderContent()}

        </Paper>
      </Grid>
    </Grid>
  }
}

export default withStyles(styles)(RequestCommunityMembership);



// WEBPACK FOOTER //
// ./src/modules/communities/components/RequestCommunityMembership.tsx